import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowLeft, FiArrowRight, FiArrowUpRight } from 'react-icons/fi';
import PropertyCard from '../PropertyCard';
import { apiUrl } from '../../config/api';
import {
  describePropertySearch,
  getRecentPropertySearches,
} from '../../services/recommendationHistory';

export default function LatestPropertiesSlider() {
  const [properties, setProperties] = useState([]);
  const [recommendationContext, setRecommendationContext] = useState(null);
  const sliderRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    const recentSearches = getRecentPropertySearches(3);

    const loadProperties = async (query = '') => {
      const params = new URLSearchParams(query);
      params.set('limit', '8');
      params.set('sort', 'newest');
      const response = await fetch(apiUrl(`/properties?${params.toString()}`));
      if (!response.ok) throw new Error('No se pudieron cargar las propiedades');
      const data = await response.json();
      return Array.isArray(data?.data) ? data.data : [];
    };

    const loadRecommendations = async () => {
      const weightedMatches = await Promise.all(
        recentSearches.map((search) => loadProperties(search.query).catch(() => []))
      );
      const scores = new Map();
      weightedMatches.forEach((matches, searchIndex) => {
        const recencyWeight = recentSearches.length - searchIndex;
        matches.forEach((property, resultIndex) => {
          const id = String(property._id);
          const current = scores.get(id) || { property, score: 0 };
          current.score += recencyWeight * 100 - resultIndex;
          scores.set(id, current);
        });
      });

      const recommended = [...scores.values()]
        .sort((a, b) => b.score - a.score)
        .map((item) => item.property);
      const newest = recommended.length < 8 ? await loadProperties() : [];
      return [...recommended, ...newest.filter((property) => (
        !recommended.some((item) => String(item._id) === String(property._id))
      ))].slice(0, 8);
    };

    loadRecommendations()
      .then((items) => {
        if (!active) return;
        setProperties(items);
        setRecommendationContext(recentSearches[0] || null);
      })
      .catch(() => {
        if (!active) return;
        setProperties([]);
        setRecommendationContext(null);
      });
    return () => { active = false; };
  }, []);

  const move = (direction) => {
    const slider = sliderRef.current;
    if (!slider) return;
    slider.scrollBy({ left: direction * Math.min(slider.clientWidth * 0.82, 440), behavior: 'smooth' });
  };

  if (!properties.length) return null;

  return (
    <Section>
      <Container>
        <Header>
          <HeaderCopy>
            <Kicker>{recommendationContext ? 'SEGÚN TUS BÚSQUEDAS RECIENTES' : 'RECIÉN PUBLICADAS'}</Kicker>
            <Title>Propiedades para vos.</Title>
            <Subtitle>
              {recommendationContext
                ? `Una selección personalizada a partir de tu interés en ${describePropertySearch(recommendationContext.query)}.`
                : 'Una selección actualizada de lugares que vale la pena conocer.'}
            </Subtitle>
          </HeaderCopy>
          <HeaderActions>
            <ExploreButton type="button" onClick={() => navigate('/buscar')}>Ver todas <FiArrowUpRight /></ExploreButton>
            <Arrow type="button" onClick={() => move(-1)} aria-label="Propiedades anteriores"><FiArrowLeft /></Arrow>
            <Arrow type="button" onClick={() => move(1)} aria-label="Propiedades siguientes"><FiArrowRight /></Arrow>
          </HeaderActions>
        </Header>

        <Slider ref={sliderRef}>
          {properties.map((property) => (
            <Slide key={property._id}><PropertyCard property={property} /></Slide>
          ))}
        </Slider>
      </Container>
    </Section>
  );
}

const Section = styled.section`
  padding: clamp(78px,9vw,130px) 0;
  background: #fcfbf8;
  border-top: 1px solid rgba(42,64,70,.1);
  border-bottom: 1px solid rgba(42,64,70,.1);
  @media(max-width:600px){padding:64px 0;}
`;
const Container = styled.div`
  max-width: 1400px; margin: 0 auto; padding: 0 clamp(20px,4.5vw,64px);
`;
const Header = styled.div`
  display: flex; align-items: flex-end; justify-content: space-between; gap: 28px; margin-bottom: clamp(32px,4vw,52px);
  @media(max-width:700px){align-items:flex-start;flex-direction:column;}
`;
const HeaderCopy = styled.div``;
const Kicker = styled.div`
  margin-bottom: 11px; color:#b45f40; font-size:12px; font-weight:750; letter-spacing:.16em;
`;
const Title = styled.h2`
  margin:0 0 13px; color:#20373c; font-size:clamp(36px,5vw,70px); font-weight:520; line-height:1; letter-spacing:-.055em;
  @media(max-width:380px){font-size:34px;}
`;
const Subtitle = styled.p`
  max-width:560px; margin:0; color:#68777b; font-size:clamp(15px,1.4vw,18px); line-height:1.6;
`;
const HeaderActions = styled.div`display:flex;align-items:center;gap:8px;flex:0 0 auto;`;
const ExploreButton = styled.button`
  display:flex;min-height:46px;align-items:center;gap:8px;margin-right:7px;padding:0 17px;color:#29474f;background:transparent;
  border:0;border-bottom:1px solid rgba(42,64,70,.4);font-size:13px;font-weight:700;cursor:pointer;
  @media(max-width:500px){display:none;}
`;
const Arrow = styled.button`
  display:grid;width:48px;height:48px;place-items:center;color:#29474f;background:#fff;border:1px solid rgba(42,64,70,.14);border-radius:50%;cursor:pointer;transition:.25s;
  &:hover{color:#fff;background:#29474f;transform:translateY(-2px);}
`;
const Slider = styled.div`
  display:flex;gap:clamp(16px,2vw,26px);overflow-x:auto;padding:5px 3px 30px;scroll-behavior:smooth;scroll-snap-type:x mandatory;scrollbar-width:none;
  &::-webkit-scrollbar{display:none;}
`;
const Slide = styled.div`
  width:clamp(330px,31vw,420px);min-width:clamp(330px,31vw,420px);scroll-snap-align:start;
  @media(max-width:500px){width:calc(100vw - 42px);min-width:calc(100vw - 42px);}
`;
